import { BentoBoxProps } from 'libs/contentful/props';
import { cn, getColorway, getYMargins } from 'utils/styles';
import BentoBoxCard from 'components/BentoBox/BentoBoxCard/BentoBoxCard';
import BentoBoxMainSection from 'components/BentoBox/BentoBoxMainSection/BentoBoxMainSection';
import BentoBoxCardIcon from 'components/BentoBox/BentoBoxCardIcon/BentoBoxCardIcon';
import BentoBoxAppReviewCard from 'components/BentoBox/BentoBoxAppReviewCard/BentoBoxAppReviewCard';
import { BentoBoxCardTypes, BentoBoxMainSectionOrientation } from 'types/constants';
import { CardGrid } from 'components/shared/Card/CardGrid';
import { liveUpdateAttrs } from 'libs/contentful/utils';

const BentoBox = ({
  sys,
  sectionName,
  headline,
  eyebrowRte,
  color,
  margins,
  copy,
  largeCard,
  cards,
  showFiveStarEyebrow,
}: BentoBoxProps) => {
  const showMainSection = copy?.json || headline?.json || eyebrowRte || showFiveStarEyebrow;
  const showMainSectionOnTop = showMainSection && largeCard;
  const showSecondGridRow = cards?.items.length > 2;
  const displayFiveStarEyebrow = showFiveStarEyebrow && !eyebrowRte;
  const spanFirstGridCard = cards?.items.length === 3 && largeCard ? true : false;

  let spanFirstCardClassNames: string;
  if (spanFirstGridCard) {
    spanFirstCardClassNames = 'sm:col-span-1 md:aspect-square sm:aspect-3/2 sm:first:col-span-2 sm:first:aspect-auto';
  }

  return (
    <section
      {...(sectionName != null && { id: encodeURIComponent(sectionName) })}
      className={`${getColorway(color)} ${getYMargins(margins)}`}>
      <div className="content-wrapper">
        {showMainSectionOnTop && (
          <div className="mb-[3.75rem]">
            <BentoBoxMainSection
              sys={sys}
              copy={copy}
              headline={headline}
              orientation={BentoBoxMainSectionOrientation.Horizontal}
              eyebrowRte={eyebrowRte}
              displayFiveStarEyebrow={displayFiveStarEyebrow}
            />
          </div>
        )}

        <div className={`flex flex-col md:flex-row ${cn({ 'md:gap-24': !largeCard, 'md:gap-6': !!largeCard })} h-full`}>
          <div className="md:basis-1/2 md:mb-0 mb-6">
            {largeCard ? (
              <BentoBoxCard
                sys={largeCard?.sys}
                copy={largeCard?.copy}
                color={largeCard.color.name}
                cardHeadlineRte={largeCard.headlineRte}
                isLargeCard
              />
            ) : (
              showMainSection && (
                <BentoBoxMainSection
                  sys={sys}
                  copy={copy}
                  headline={headline}
                  orientation={BentoBoxMainSectionOrientation.Vertical}
                  eyebrowRte={eyebrowRte}
                  displayFiveStarEyebrow={displayFiveStarEyebrow}
                />
              )
            )}
          </div>
          <div className="flex flex-col md:basis-1/2 gap-4" {...liveUpdateAttrs(sys?.id, 'cards')}>
            <CardGrid
              className={`gap-4 sm:gap-4 grid-cols-2 grid-flow-row sm:grid-rows-auto ${cn({ 'sm:grid-rows-2': showSecondGridRow })}`}>
              {cards.items.map((card, idx) => {
                const sharedProps = {
                  sys: card.sys,
                  color: card.color.name,
                  copy: card?.copy,
                };
                if (card.__typename === BentoBoxCardTypes.AppReview) {
                  return (
                    <BentoBoxAppReviewCard
                      key={idx}
                      {...sharedProps}
                      cardHeadlineRte={card.cardHeadlineRte}
                      stars={card.numberOfStars}
                    />
                  );
                }
                if (card.icon) {
                  return (
                    <BentoBoxCardIcon
                      key={idx}
                      icon={card.icon}
                      aspectRatio={card.aspectRatio}
                      cardHeadlineRte={card.cardHeadlineRte}
                      headlineSize={card.cardHeadlineSize}
                      {...sharedProps}
                      classNames={spanFirstCardClassNames}
                    />
                  );
                }
                return (
                  <BentoBoxCard
                    key={idx}
                    cardHeadlineRte={card.cardHeadlineRte}
                    headlineSize={card.cardHeadlineSize}
                    {...sharedProps}
                    classNames={spanFirstCardClassNames}
                  />
                );
              })}
            </CardGrid>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BentoBox;
